import BaseNumberVTextField, { enforceFormat } from './BaseNumberVTextField'
import { parseNumericFieldValue } from '~/cynqueModules/dataObjects/Numeric'

export const MAXIMUM_FRACTION_DIGITS = 6

const formatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  maximumFractionDigits: MAXIMUM_FRACTION_DIGITS,
})

export function enforcePercentageFormat(event: unknown, amount: any) {
  // amount comes from ifield.value which is fractional when the field first receives
  // focus (e.g. 0.29), but becomes a typed string after the first keystroke (e.g. "29").
  // Convert fractional numbers to whole numbers so enforceFormat checks against
  // the displayed value (e.g. 29) — otherwise "0.29".includes('.') blocks '.' input.
  const displayAmount =
    typeof amount === 'number'
      ? parseFloat((amount * 100).toPrecision(12))
      : amount
  enforceFormat(event, displayAmount, MAXIMUM_FRACTION_DIGITS)
}

// Whole-number string for a model value. A fractional NUMBER (0.29, from the API or our
// own blur emit) is scaled ×100 to the displayed whole number (29); a string (mid-typing,
// e.g. "29") is already whole.
function toWholeString(value: unknown): string {
  if (value === '' || value === null || value === undefined) {
    return ''
  }
  return typeof value === 'number'
    ? String(parseFloat((value * 100).toPrecision(12)))
    : String(value)
}

// Vuetify 3 migration: wraps <v-text-field> via BaseNumberVTextField, but the percentage
// is stored as a FRACTION (0.29) and shown as a whole number (29 while editing, "29%"
// blurred). It overrides the three model<->display hooks to do the ÷100 / ×100 conversion
// the Vuetify-2 showValue used to do. blurredFormat receives a WHOLE number (29) -> "29%".
export default {
  extends: BaseNumberVTextField,
  props: {
    numericType: {
      type: String,
      default: 'percentageTextField',
    },
    blurredFormat: {
      type: Function,
      default: (v: string) => {
        const num = parseFloat(v as unknown as string)
        return Number.isFinite(num) ? formatter.format(num) + '%' : v
      },
    },
  },
  methods: {
    // At rest / external update: fractional model -> "29%".
    formatForDisplay(raw: unknown): string {
      const self = this as any
      if (raw === '' || raw === null || raw === undefined) {
        return ''
      }
      const parsed = parseNumericFieldValue(raw, self.numericType)
      if (parsed === null) {
        return String(raw)
      }
      // A fractional NUMBER model is scaled to a whole number for display; a typed
      // string is already whole.
      const whole =
        typeof raw === 'number'
          ? parseFloat((parsed * 100).toPrecision(12))
          : parsed
      return self.blurredFormat(whole) ?? ''
    },
    // On focus: expand the fraction to a whole number for editing (0.29 -> "29").
    rawForEditing(): string {
      return toWholeString((this as any).modelValue)
    },
    // On blur: commit the FRACTION to the model (typed "29" -> 0.29) and format the
    // display ("29%"). Genuinely-invalid input is left verbatim (no fraction emitted).
    commitBlur() {
      const self = this as any
      if (self.display === '.') {
        self.$emit('clearModelValue')
      }
      const parsed = parseNumericFieldValue(self.display, self.numericType)
      if (parsed === null) {
        self.$nextTick(() => self.$refs.field?.validate?.())
        return
      }
      const fractional = parseFloat((parsed / 100).toPrecision(12))
      self.$emit('update:modelValue', fractional)
      self.display = self.blurredFormat(parsed) ?? ''
      self.$nextTick(() => self.$refs.field?.validate?.())
    },
  },
}
